import { useEffect } from "react";

export interface ToastAction {
  label: string;
  onClick: () => void;
  variant?: "primary" | "danger" | "secondary";
}

export interface ToastData {
  id: string;
  message: string;
  type: "permanent" | "temporary";
  actions?: ToastAction[];
  duration?: number;
  /** Set on join-request toasts so consumers can identify the associated request
   * by a structured field rather than parsing the id string. */
  joinRequestId?: number;
}

interface ToastProps {
  toasts: ToastData[];
  onDismiss: (id: string) => void;
}

function ToastItem({ toast, onDismiss }: { toast: ToastData; onDismiss: () => void }) {
  useEffect(() => {
    if (toast.type === "temporary") {
      const timer = setTimeout(onDismiss, toast.duration ?? 3000);
      return () => clearTimeout(timer);
    }
  }, [toast, onDismiss]);

  return (
    <div className="toast-item" role="alert">
      <span className="toast-message">{toast.message}</span>
      {toast.actions && toast.actions.length > 0 && (
        <div className="toast-actions">
          {toast.actions.map((action, i) => (
            <button
              key={i}
              className={`toast-btn toast-btn-${action.variant ?? "primary"}`}
              onClick={action.onClick}
            >
              {action.label}
            </button>
          ))}
        </div>
      )}
      <button className="toast-close" onClick={onDismiss} aria-label="Close">&times;</button>
    </div>
  );
}

export function Toast({ toasts, onDismiss }: ToastProps) {
  if (toasts.length === 0) return null;

  return (
    <div className="toast-container" aria-live="polite">
      {toasts.map((toast) => (
        <ToastItem key={toast.id} toast={toast} onDismiss={() => onDismiss(toast.id)} />
      ))}
    </div>
  );
}
